import { FunctionComponent, memo, useContext, useEffect, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { UserContext } from '../../../context';
import { LeaderBoardUserBadge, useLeaderBoardMeQuery } from '../../../services';
import { LastDateValue, setCssVar, useLastDate } from '../../../shared';
import { renderPositionCell, renderPositionDiffCell, renderUserCell } from '../helpers';

import classNames from 'classnames';
import classes from '../LeaderBoard.module.scss';

interface SelfStatsProps {
  leaderBoardBages: LeaderBoardUserBadge[];
}

export const SelfStats: FunctionComponent<SelfStatsProps> = memo(({ leaderBoardBages }) => {
  const { t } = useTranslation();

  const { getLastDate } = useLastDate();

  const { from, to } = useMemo(() => getLastDate(LastDateValue.LAST_30_DAYS), [getLastDate]);

  const filters = useMemo(() => {
    return { from: from ?? '', to: to ?? '' };
  }, [from, to]);

  const { data, isFetching } = useLeaderBoardMeQuery({ filters });

  const { userProfile } = useContext(UserContext);

  const { id: badgeId, name: badgeName } = { ...userProfile.leaderBoardBadge };

  const renderRank = useMemo(() => Boolean(leaderBoardBages.length), [leaderBoardBages.length]);

  const rankAsColumn = useMemo(() => renderRank && !isMobileOnly, [renderRank]);

  const rankWithName = useMemo(() => renderRank && isMobileOnly, [renderRank]);

  const rank = useMemo(() => t(`leaderBoardBadges.${badgeName}`), [badgeName, t]);

  const renderProgresBar = useMemo(() => {
    if (isMobileOnly || !renderRank) {
      return null;
    }

    return <div className={classes['leader-board__table-progress-bar']}></div>;
  }, [renderRank]);

  useEffect(() => {
    if (!leaderBoardBages.length) {
      return;
    }

    const points = data?.points ?? 0;

    const rankIndex = leaderBoardBages.findIndex(({ id }) => id === badgeId);

    if (rankIndex === -1) {
      return;
    }

    if (rankIndex === leaderBoardBages.length - 1) {
      setCssVar('--rank-percentage', '100%');
      return;
    }

    const { name: nextBadgeName, level: nextBadgeLevel } = leaderBoardBages[rankIndex + 1];

    setCssVar('--rank-percentage', `${(points / nextBadgeLevel) * 100}%`);

    setCssVar(
      '--rank-percentage-hint',
      `'${t('leaderBoard.progress-hint', {
        count: nextBadgeLevel - points,
        level: t(`leaderBoardBadges.${nextBadgeName}`),
      })}'`
    );
  }, [badgeId, data?.points, leaderBoardBages, t]);

  if (isFetching) {
    return <Skeleton height={'4.5rem'} />;
  }

  if (!data) {
    return null;
  }

  const { position, points, positionDiff } = data;

  // * position is -1 for admin / editor which should be excluded
  if (position < 0) {
    return null;
  }

  return (
    <div className={classes['leader-board__table-wrapper']}>
      <table
        className={classNames(
          classes['leader-board__table'],
          classes['leader-board__table--self-stats']
        )}
      >
        <colgroup>
          <col width={'40'} />
          <col width={isMobileOnly ? '170' : '300'} />
          <col width={isMobileOnly ? '60' : '140'} />
          {rankAsColumn && <col width={'180'} />}
          <col width={isMobileOnly ? '70' : '80'} />
        </colgroup>
        <tbody>
          <tr>
            {renderPositionCell({ position, isActive: true, color: '#fff' })}
            {renderUserCell({ user: userProfile, ...(rankWithName && { rank }) })}
            <td>
              {points} {renderProgresBar}
            </td>
            {rankAsColumn && <td>{rank}</td>}
            <td>{renderPositionDiffCell(positionDiff)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});
